import { DEFAULT_TAX_PARAMS } from '@modules/controladoria-v2/constants/taxParams'
import { buildInitialState } from './state'

const cloneDeep = (value) => JSON.parse(JSON.stringify(value))

// ---------- Fechamentos ----------

export const SET_FECHAMENTOS = (state, payload) => {
  if (!state || !state.fechamentos) return
  state.fechamentos.data = Array.isArray(payload) ? cloneDeep(payload) : []
}

export const ADD_FECHAMENTO = (state, payload) => {
  if (!state || !state.fechamentos || !payload) return
  const data = state.fechamentos.data || []
  const idx = data.findIndex(
    (f) => f.data_referencia === payload.data_referencia
  )
  if (idx >= 0) {
    state.fechamentos.data[idx] = { ...data[idx], ...payload }
  } else {
    state.fechamentos.data = [cloneDeep(payload), ...data]
  }
}

export const SET_FECHAMENTO_ATUAL = (state, payload) => {
  if (!state) return
  const nextDataReferencia =
    payload &&
    typeof payload.dataReferencia === 'string' &&
    payload.dataReferencia
      ? payload.dataReferencia
      : null
  const previousDataReferencia =
    state.fechamentoAtual && state.fechamentoAtual.dataReferencia
      ? state.fechamentoAtual.dataReferencia
      : null
  state.fechamentoAtual = { dataReferencia: nextDataReferencia }
  // Quando muda o fechamento, invalida o cache de importações — o próximo
  // carregar() refetcha os sumários para a nova data_referencia. Listas
  // também são por mês, então resetamos o cache pra evitar vazar dados do
  // mês anterior até o próximo carregar.
  if (nextDataReferencia !== previousDataReferencia) {
    state.importacoes = {}
    state.importacoesDataReferencia = null
    // Status de processamento do dataroom é por mês — invalida pra não vazar o
    // status do mês anterior até o próximo polling.
    state.dataroomStatus = { data: {}, loading: false, lastUpdate: null }
    state.listas = { data: [], dataReferencia: null, loading: false }
    // Opções de dropdown incluem as listas do fechamento — invalida o cache
    // pra não vazar listas do mês anterior até o próximo carregar.
    state.configDREDropdownOptions = {}
    state.configDREDropdownOptionsDataReferencia = null
    // Status dos motores é por mês. Sem invalidar aqui, as pills do header
    // continuam mostrando o resultado do mês anterior durante o GET do mês
    // novo — o usuário lê status de fevereiro achando que é de março.
    state.engineStatus = {
      data: cloneDeep(buildInitialState().engineStatus.data),
      loading: false,
      dataReferencia: null,
      error: null,
    }
  }
}

// ---------- Importação ----------

export const SET_IMPORTACOES = (state, payload) => {
  if (!state || !payload || typeof payload !== 'object') return
  state.importacoes =
    payload.value && typeof payload.value === 'object'
      ? cloneDeep(payload.value)
      : {}
  state.importacoesDataReferencia =
    typeof payload.dataReferencia === 'string' && payload.dataReferencia
      ? payload.dataReferencia
      : null
}

export const SET_IMPORTACAO_STATUS = (state, payload) => {
  if (!state || !state.importacoes || !payload || !payload.key) return
  const current = state.importacoes[payload.key] || {}
  state.importacoes = {
    ...state.importacoes,
    [payload.key]: { ...current, ...payload.patch },
  }
}

export const RESET_IMPORTACAO = (state) => {
  if (!state) return
  state.importacoes = {}
  state.importacoesDataReferencia = null
}

// Substitui o mapa inteiro de status do dataroom (`{ tipo: {...} }`) dentro do
// slice padrão `{ data, loading, lastUpdate }`. Payload é o próprio mapa já
// normalizado pela camada de API (origem → tipo).
export const SET_DATAROOM_STATUS = (state, payload) => {
  if (!state || !state.dataroomStatus) return
  state.dataroomStatus.data =
    payload && typeof payload === 'object' && !Array.isArray(payload)
      ? cloneDeep(payload)
      : {}
  state.dataroomStatus.lastUpdate = new Date()
}

// ---------- ConfigDRE grids ----------

export const SET_CONFIG_GRID = (state, payload) => {
  if (!state || !state.configDRE || !payload || !payload.tabKey) return
  const rows = Array.isArray(payload.rows) ? cloneDeep(payload.rows) : []
  state.configDRE = { ...state.configDRE, [payload.tabKey]: rows }
}

export const RESET_CONFIG_GRID = (state, payload) => {
  if (!state || !state.configDRE || !payload || !payload.tabKey) return
  // Sem seed: apenas remove a chave para que o próximo carregar reidrate a
  // partir do backend.
  const next = { ...state.configDRE }
  delete next[payload.tabKey]
  state.configDRE = next
}

// ---------- ConfigDRE dropdown options ----------

// Substitui o dicionário inteiro `{ campo: [valores] }` vindo do backend e
// registra a `data_referencia` a que ele pertence (as opções de rateio incluem
// as listas do fechamento). Payload: `{ value, dataReferencia }`.
export const SET_CONFIG_DRE_DROPDOWN_OPTIONS = (state, payload) => {
  if (!state || !payload || typeof payload !== 'object') return
  const value = payload.value
  state.configDREDropdownOptions =
    value && typeof value === 'object' && !Array.isArray(value)
      ? cloneDeep(value)
      : {}
  state.configDREDropdownOptionsDataReferencia =
    typeof payload.dataReferencia === 'string' && payload.dataReferencia
      ? payload.dataReferencia
      : null
}

// ---------- Listas ----------

export const SET_LISTAS = (state, payload) => {
  if (!state || !state.listas || !payload || typeof payload !== 'object') return
  state.listas.data = Array.isArray(payload.value) ? cloneDeep(payload.value) : []
  state.listas.dataReferencia =
    typeof payload.dataReferencia === 'string' && payload.dataReferencia
      ? payload.dataReferencia
      : null
}

export const SET_LISTAS_LOADING = (state, payload) => {
  if (!state || !state.listas) return
  state.listas.loading = Boolean(payload)
}

// ---------- Engines ----------

export const SET_ENGINE_STATUS = (state, payload) => {
  if (!state || !state.engineStatus || !payload || !payload.engineId) return
  const current = state.engineStatus.data[payload.engineId] || {}
  state.engineStatus.data = {
    ...state.engineStatus.data,
    [payload.engineId]: { ...current, ...payload.payload },
  }
}

// Substitui o mapa inteiro e carimba a `data_referencia` que o produziu — é o
// carimbo que permite à UI distinguir status do mês atual de resíduo do mês
// anterior. Payload: `{ value, dataReferencia }`, mesmo contrato de SET_LISTAS.
export const REPLACE_ENGINE_STATUS = (state, payload) => {
  if (!state || !state.engineStatus || !payload || typeof payload !== 'object') {
    return
  }
  const value = payload.value
  if (!value || typeof value !== 'object' || Array.isArray(value)) return
  state.engineStatus.data = cloneDeep(value)
  state.engineStatus.dataReferencia =
    typeof payload.dataReferencia === 'string' && payload.dataReferencia
      ? payload.dataReferencia
      : null
  state.engineStatus.error = null
}

export const SET_ENGINE_STATUS_LOADING = (state, payload) => {
  if (!state || !state.engineStatus) return
  state.engineStatus.loading = Boolean(payload)
}

// Mensagem da última falha de carga do status (ou null ao limpar). Sem isso a
// UI ficaria em shimmer permanente quando o GET falha.
export const SET_ENGINE_STATUS_ERROR = (state, payload) => {
  if (!state || !state.engineStatus) return
  state.engineStatus.error =
    typeof payload === 'string' && payload ? payload : null
}

export const RESET_ENGINE_STATUS = (state) => {
  if (!state || !state.engineStatus) return
  state.engineStatus = cloneDeep(buildInitialState().engineStatus)
}

// ---------- Parâmetros de impostos ----------

// Faz merge apenas das chaves conhecidas (`provisao_impostos_automatica`,
// `possui_simples_nacional`) coagindo o valor para boolean. Ignora chaves
// desconhecidas vindas do backend para não poluir o slice.
export const SET_TAX_PARAMS = (state, payload) => {
  if (!state || !payload || typeof payload !== 'object') return
  const next = { ...DEFAULT_TAX_PARAMS, ...state.taxParams }
  Object.keys(DEFAULT_TAX_PARAMS).forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(payload, key)) {
      next[key] = Boolean(payload[key])
    }
  })
  state.taxParams = next
}

// ---------- Versão ----------

export const INCREMENT_VERSOES_PUBLICADAS = (state) => {
  if (!state) return
  state.versoesPublicadas = (state.versoesPublicadas || 0) + 1
}

// ---------- Reset ----------

export const RESET_STATE = (state) => {
  if (!state) return
  const fresh = buildInitialState()
  Object.keys(fresh).forEach((key) => {
    state[key] = fresh[key]
  })
}

// Alias para o contrato uniforme de reset global (resetAllModules commita
// `${namespace}/RESET`). [CU-86ajnmt4r]
export const RESET = RESET_STATE
